import React, { useState } from "react";
import {
  Box,
  ButtonBase,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import { blue, green, grey } from "@mui/material/colors";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useTranslation } from "react-i18next";

import LetterBadge from "../common/LetterBadge";
import {
  CAMPAIGN_STATUS_DESCRIPTIONS,
  CAMPAIGN_STATUS_LABELS,
} from "../common/StatusChips";
import { components } from "../../types/api";

type CampaignStatus =
  components["schemas"]["_36_Enums.AssociatedCampaignStatus"];

const STATUS_ORDER: CampaignStatus[] = [
  "DRAFT",
  "PENDING",
  "READY",
  "ACTIVE",
  "FINALIZED",
];

// Colors specific to this badge (they differ from the generic status chip):
// Draft grey, Paused black, Ready blue, Active green, Finalized dark green.
const BADGE_COLORS: Record<CampaignStatus, { bg: string; fg: string }> = {
  DRAFT: { bg: grey[400], fg: grey[900] },
  PENDING: { bg: "#111111", fg: "#ffffff" },
  READY: { bg: blue[600], fg: "#ffffff" },
  ACTIVE: { bg: green[500], fg: "#ffffff" },
  FINALIZED: { bg: green[900], fg: "#ffffff" },
};

type CampaignStatusBadgeProps = {
  status: CampaignStatus;
  /** Si se pasa, el badge se puede pulsar para elegir otro estado. */
  onChange?: (status: CampaignStatus) => void;
  disabled?: boolean;
  /** Estados que no se ofrecen en el menú. */
  excludedStatuses?: CampaignStatus[];
  /** Pill with the badge plus the status name (e.g. in a page header). */
  showLabel?: boolean;
};

/**
 * Estado de una campaña asociada como un círculo con una letra y un color
 * por estado. El nombre y la descripción van en el tooltip.
 */
const CampaignStatusBadge: React.FC<CampaignStatusBadgeProps> = ({
  status,
  onChange,
  disabled,
  excludedStatuses,
  showLabel = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const label = (s: CampaignStatus) =>
    t(`briefingsView.campaignStatus.${s}`, CAMPAIGN_STATUS_LABELS[s] ?? s);

  const isDark = theme.palette.mode === "dark";
  const { bg, fg } = BADGE_COLORS[status] ?? BADGE_COLORS.DRAFT;

  const badge = (
    <LetterBadge
      letter={t(
        `briefingsView.campaignStatusShort.${status}`,
        status.charAt(0)
      )}
      title={`${label(status)} — ${t(
        `briefingsView.campaignStatusDescription.${status}`,
        CAMPAIGN_STATUS_DESCRIPTIONS[status] ?? ""
      )}`}
      sx={{
        backgroundColor: bg,
        color: fg,
        // Black on the dark theme's near-black surface needs an outline to
        // be seen at all.
        ...(status === "PENDING" &&
          isDark && { boxShadow: `inset 0 0 0 1px ${grey[600]}` }),
        cursor: onChange ? "pointer" : "default",
        opacity: disabled ? 0.5 : 1,
      }}
    />
  );

  const pill = showLabel && (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        pl: 0.4,
        pr: onChange ? 0.5 : 1.25,
        py: 0.4,
        borderRadius: 999,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: "background.paper",
        opacity: disabled ? 0.5 : 1,
        transition: "background-color .15s",
        ...(onChange && { "&:hover": { bgcolor: "action.hover" } }),
      }}
    >
      {badge}
      <Typography variant="body2" fontWeight={600} component="span">
        {label(status)}
      </Typography>
      {onChange && (
        <ArrowDropDownIcon fontSize="small" sx={{ color: "text.secondary" }} />
      )}
    </Box>
  );

  if (!onChange) return pill || badge;

  return (
    <>
      <ButtonBase
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        onDoubleClick={(e) => e.stopPropagation()}
        disabled={disabled}
        aria-label={t("briefingsView.changeCampaignStatus", "Change status")}
        sx={{ borderRadius: showLabel ? 999 : "50%" }}
      >
        {pill || badge}
      </ButtonBase>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        onClick={(e) => e.stopPropagation()}
      >
        {STATUS_ORDER.filter((s) => !excludedStatuses?.includes(s)).map((s) => (
          <MenuItem
            key={s}
            selected={s === status}
            onClick={() => {
              setAnchorEl(null);
              if (s !== status) onChange(s);
            }}
          >
            <ListItemText primary={label(s)} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default CampaignStatusBadge;
